import React from 'react';
import SectionTitle from './SectionTitle';
import Badge from './Badge';

const Solutions = () => {
  const solutions = [
    {
      id: 1,
      title: 'Retail Ops Suite',
      description: 'Complete point-of-sale and inventory management system',
      metrics: [
        { label: 'Stock Accuracy', value: '↑ 95%' },
        { label: 'Shrinkage Reduction', value: '↓ 40%' },
        { label: 'Multi-store Visibility', value: 'Real-time' }
      ],
      features: [
        'Barcode scanning and inventory tracking',
        'Multi-location management',
        'Sales analytics and reporting',
        'Staff management and permissions'
      ],
      icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z'
    },
    {
      id: 2,
      title: 'Education / HEI Tools',
      description: 'Secure student and faculty management portal',
      metrics: [
        { label: 'Processing Time', value: '↓ 70%' },
        { label: 'Compliance Rate', value: '↑ 100%' },
        { label: 'User Satisfaction', value: '↑ 90%' }
      ],
      features: [
        'Student enrollment and records',
        'Grade management system',
        'Faculty portal and scheduling',
        'Compliance reporting tools'
      ],
      icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
    },
    {
      id: 3,
      title: 'SME Growth Stack',
      description: 'All-in-one business management platform',
      metrics: [
        { label: 'Setup Time', value: '2-4 weeks' },
        { label: 'Cost Savings', value: '↑ 60%' },
        { label: 'Productivity', value: '↑ 80%' }
      ],
      features: [
        'Website and online presence',
        'CRM and lead management',
        'Invoicing and payments',
        'Analytics and insights'
      ],
      icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6'
    }
  ];

  return (
    <section id="solutions" className="py-16 sm:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          subtitle="Industry Solutions"
          title="Purpose-Built Systems for Your Sector"
          className="mb-16"
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {solutions.map((solution, index) => (
            <div
              key={solution.id}
              className="group relative bg-white rounded-2xl border border-neutral-200/60 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden hover:-translate-y-2"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              {/* Header */}
              <div className="p-8 pb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center group-hover:bg-indigo-600 transition-colors duration-300">
                    <svg
                      className="w-6 h-6 text-indigo-600 group-hover:text-white transition-colors duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d={solution.icon}
                      />
                    </svg>
                  </div>
                  <Badge variant="success" size="sm">
                    {solution.id === 1 ? 'Retail' : solution.id === 2 ? 'Education' : 'SME'}
                  </Badge>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors duration-300">
                  {solution.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {solution.description}
                </p>

                {/* Metrics */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {solution.metrics.map((metric, metricIndex) => (
                    <div key={metricIndex} className="text-center">
                      <div className="text-lg font-bold text-indigo-600">{metric.value}</div>
                      <div className="text-xs text-gray-500">{metric.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Features */}
              <div className="px-8 pb-8">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Key Features:</h4>
                <ul className="space-y-2">
                  {solution.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-3">
                      <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/0 to-indigo-50/0 group-hover:from-indigo-50/30 group-hover:to-indigo-50/10 transition-all duration-300 pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <div className="bg-white rounded-2xl border border-neutral-200/60 shadow-sm p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Transform Your Operations?
            </h3>
            <p className="text-gray-600 mb-6">
              Let's discuss how our solutions can streamline your business processes and drive growth.
            </p>
            <button
              onClick={() => {
                const element = document.querySelector('#contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Start Your Project
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solutions;


